import { selectCanvasElements, removeElementsByUuid } from '@/utils/elementsUtils';
import { handlePageEpsStatus } from '@/utils/elementsPageEpsUtils';
import { duplicateItemsDeep } from '@/utils/layers/duplicateUtils';
import { removeItemsDeep } from '@/utils/layers/removeUtils';
import { groupItemsDeep, unGroupItemsDeep } from '@/utils/layers/groupUtils';
import { calculateCanvasDimensions } from '@/utils/domUtils';
import { ResizeSensor } from 'css-element-queries';
import { throttle } from 'underscore';
import { cloneDeep } from 'lodash';
import { FabricAlignmentGuidelines } from './fabric.guidelines';
import { FabricClipboard } from './fabric.clipboard';
import { FabricContextMenu } from './fabric.contextmenu';

const SCROLLBAR_WIDTH_PX = 10; // This should match the scrollbar size set in CSS

(function () {
    ('use strict');

    const handleSelectionMovement = (objects, direction, canvas, amount) => {
        const moveObject = (object) => {
            let localAmount = amount;
            // If object is locked, don't move it
            if (object.locked) {
                return;
            }

            // If DPI is available, convert the amount with the DPI value
            if (canvas.DPI) {
                const mmPerInch = 25.4;
                localAmount = (localAmount * canvas.DPI) / mmPerInch;
            }

            if (direction === 'left' || direction === 'up') {
                localAmount = -localAmount;
            }

            if (direction === 'left' || direction === 'right') {
                object.set('left', object.left + localAmount);
            } else {
                object.set('top', object.top + localAmount);
            }

            object.setCoords();
            object.fire('modified', object);
            if (object?.isTextPath) {
                object.fire('moving', object);
            }
        };

        if (objects) {
            if (objects.type === 'activeSelection') {
                for (const object of objects._objects ?? [objects]) {
                    moveObject(object);
                }
            } else if (objects.length) {
                for (const object of objects) {
                    moveObject(object);
                }
            } else if (objects) {
                moveObject(objects);
            }
            canvas.renderAll();
        }
    };

    const respondToVisibility = (element, callback) => {
        const options = {
            root: document.documentElement
        };

        const observer = new IntersectionObserver((entries) => {
            entries.forEach((entry) => {
                callback(entry.intersectionRatio > 0);
            });
        }, options);

        observer.observe(element);
        return observer;
    };

    // Fabric.js custom text object
    fabric.util.pxToPt = function (px) {
        return (px * 72) / fabric.DPI;
    };

    fabric.perfLimitSizeTotal = fabric.maxCacheSideLimit ** 2;
    fabric.CanvasEx = fabric.util.createClass(fabric.Canvas, {
        disposed: false,
        isPreviewStep: false,
        enableGroups: true,
        enablePageEps: false,

        centeredKey: 'shiftKey',
        uniScaleKey: 'none',
        altActionKey: 'ctrlKey',
        allowedElementsToCopy: [
            'bounded-text',
            'text-transform',
            'path-itext',
            'image-placeholder',
            'vector-placeholder'
        ],

        lastClipboardObject: null,
        lastElementIdCalculator: null,
        canDeleteObject: null,
        enableCopyPaste: false,

        fullVisibilityZoom: 1,
        minZoom: 0.1,
        maxZoom: 10,
        preventOutsideArtworkZoom: true,
        enableZoom: true,

        // class instance for undoManager
        undoManager: null,

        // Limits for panning, this is expressed as workspace size multiples. The largest workspace dimension is used.
        // So, 3 here means limit pan to 3 times the artwork width or height (the largest one)
        panLimit: 0.05,

        imageSmoothingEnabled: true,

        _workspaceExtraScalingX: 1,
        _workspaceExtraScalingY: 1,

        _artworkWidth: 0,
        _artworkHeight: 0,
        _artworkOffsetX: 0,
        _artworkOffsetY: 0,
        _workspaceWidth: 0,
        _workspaceHeight: 0,

        limitCanvasSize: false,
        maxCanvasSize: 1024,
        minCanvasSize: 1024,

        bgShadowColor: 'rgba(0, 0, 0, 0.2)',
        bgShadowBlur: 40,

        _zoomContainerEl: null,
        _zoomDropdownEl: null,
        _zoomTextEl: null,
        _resizeSensor: null,
        _visibilitySensor: null,
        canvasElWidthMm: 0,
        zoomOptions: ['Fit', 'Fill', 10, 25, 50, 75, 100, 150, 200, 300],

        _scroll: null,
        _disableVerticalScroll: false,
        _disableHorizontalScroll: false,
        seamLessScrollbars: false,

        displayGuidelines: false,
        _guidelines: null,

        _clipboard: null,

        enableContextMenu: false,
        _contextMenu: null,

        DPI: 96,
        artworkTitle: '',

        initialize: function (canvas, options) {
            if (options && options.eventContainer) {
                const $container = $('#' + options.eventContainer);
                const el = $container.get(0);
                if (el) {
                    el.tabIndex = 1000;
                }

                $container.on('keydown', async (event) => {
                    const key = event.key;
                    const ctrlKeyPressed = event.ctrlKey || event.metaKey;
                    const shiftKeyPressed = event.shiftKey;

                    if (
                        (typeof options.enableCopyPaste !== 'function' && options.enableCopyPaste) ||
                        (typeof options.enableCopyPaste === 'function' &&
                            options.enableCopyPaste(this.getActiveObjects()))
                    ) {
                        // Ctrl + B (same as Ctrl + V but doesn't shift the pasted element)
                        if (key.toLowerCase() === 'b' && ctrlKeyPressed) {
                            await this._cloneSelection(this.lastClipboardObject, {
                                shiftElements: false,
                                updateElementId: true,
                                updateName: false,
                                appendId: true,
                                saveHistory: true
                            });
                        }
                        // Ctrl + C
                        else if (key.toLowerCase() === 'c' && ctrlKeyPressed) {
                            await this.copySelection();
                        }
                        // Ctrl + D
                        else if (key.toLowerCase() === 'd' && ctrlKeyPressed) {
                            // Stop the event so it doesn't trigger the bookmark add shortcut!
                            event.preventDefault();
                            event.stopPropagation();

                            await this._cloneSelection();
                        }
                        // Ctrl + G (elements grouping)
                        else if (key.toLowerCase() === 'g' && ctrlKeyPressed && !shiftKeyPressed) {
                            // Stop the event so it doesn't trigger the search shortcut!
                            event.preventDefault();
                            event.stopPropagation();

                            this.groupSelectedItems();
                        }
                        // Ctrl + Shift + G (elements unGroup)
                        else if (key.toLowerCase() === 'g' && ctrlKeyPressed && shiftKeyPressed) {
                            // Stop the event so it doesn't trigger the search shortcut!
                            event.preventDefault();
                            event.stopPropagation();

                            this.unGroupSelectedItems();
                        }
                    }

                    // Left arrow
                    if (key === 'ArrowLeft') {
                        handleSelectionMovement(this.getActiveObject(), 'left', this, this.DPI ? 1 : 20);
                    }
                    // Right arrow
                    else if (key === 'ArrowRight') {
                        handleSelectionMovement(this.getActiveObject(), 'right', this, this.DPI ? 1 : 20);
                    }
                    // Up arrow
                    else if (key === 'ArrowUp') {
                        handleSelectionMovement(this.getActiveObject(), 'up', this, this.DPI ? 1 : 20);
                    }
                    // Down arrow
                    else if (key === 'ArrowDown') {
                        handleSelectionMovement(this.getActiveObject(), 'down', this, this.DPI ? 1 : 20);
                    }
                    // Delete selected objects
                    else if (event.key === 'Delete') {
                        this.deleteSelection();
                    }
                    // Shift + F (Fit to canvas)
                    else if (key.toLowerCase() === 'f' && shiftKeyPressed) {
                        event.preventDefault();
                        event.stopPropagation();

                        this.elementsSelectedAlign('fit', 'selection', options.editMode);

                        window.DD_RUM?.addAction('Fit to canvas - hotkey');
                    }

                    if (this.undoManager && ctrlKeyPressed) {
                        // Ctrl + Z (Undo canvas action)
                        if (key.toLowerCase() === 'z' && !shiftKeyPressed) {
                            this.undoManager.undo();
                        }
                        // Ctrl + Y || Ctrl + Shift + Z  (Redo canvas action)
                        else if (key.toLowerCase() === 'y' || (key.toLowerCase() === 'z' && shiftKeyPressed)) {
                            this.undoManager.redo();
                        }
                    }
                });
            }

            this.callSuper('initialize', canvas, options);

            this._initCloneEvents?.();
            this._scroll = {
                horizontalScrollbarEl: null,
                verticalScrollbarEl: null,
                moveListeners: [],
                upListeners: [],
                downListeners: []
            };
            this.elementsGroups = [];
            this._selectedGroups = [];
            this.elementsPageEps = [];
            this._selectedPageEps = [];
            this._selectedItems = [];

            if (this.enableZoom) {
                // Set the zoom event listeners BEFORE fabric.js listener so we can prevent them from running
                this.removeListeners();

                this._zoomWheelListener = this._zoomWheelListener.bind(this);
                this._zoomMouseDownListener = this._zoomMouseDownListener.bind(this);
                this._zoomMouseMoveListener = this._zoomMouseMoveListener.bind(this);
                this._zoomMouseUpListener = this._zoomMouseUpListener.bind(this);

                window.addEventListener(
                    'wheel',
                    (e) => {
                        // Prevent tab zoom
                        if (e.ctrlKey) {
                            e.preventDefault();
                        }
                    },
                    {
                        passive: false
                    }
                );

                this.upperCanvasEl.addEventListener('wheel', this._zoomWheelListener, { passive: false });
                this.upperCanvasEl.addEventListener('mousedown', this._zoomMouseDownListener, { passive: false });
                this.upperCanvasEl.addEventListener('mousemove', this._zoomMouseMoveListener, { passive: false });
                window.addEventListener('mouseup', this._zoomMouseUpListener, { passive: false });

                this._initEventListeners();

                // Add zoom indication element
                this._zoomContainerEl = document.createElement('div');
                this._zoomContainerEl.classList.add('zoom-container');

                const zoomViewerEl = document.createElement('div');
                zoomViewerEl.classList.add('zoom-viewer');

                this._zoomTextEl = document.createElement('div');
                this._zoomTextEl.classList.add('zoom-text');

                const zoomIconEl = document.createElement('div');
                zoomIconEl.classList.add('zoom-icon');
                const faIcon = document.createElement('i');
                faIcon.classList.add('fa', 'fa-search');
                zoomIconEl.append(faIcon);

                zoomViewerEl.append(zoomIconEl);
                zoomViewerEl.append(this._zoomTextEl);

                if (this.zoomOptions && this.zoomOptions.length) {
                    this._zoomDropdownEl = document.createElement('div');
                    this._zoomDropdownEl.classList.add('zoom-dropdown');
                    this._zoomDropdownEl.style.display = 'none';

                    for (const option of this.zoomOptions) {
                        const optionEl = document.createElement('div');
                        optionEl.classList.add('zoom-option');

                        if (
                            typeof option === 'string' &&
                            (option.toLowerCase() === 'fit' || option.toLowerCase() === 'fill')
                        ) {
                            optionEl.innerHTML = option;
                            $(optionEl).on('click', (event) => {
                                if (option.toLowerCase() === 'fit') {
                                    // Fit zoom
                                    this.setEffectiveZoom(1);
                                } else {
                                    // Fill zoom
                                    const ratio = Math.max(
                                        this._workspaceHeight / this._artworkHeight,
                                        this._workspaceWidth / this._artworkWidth
                                    );
                                    this.setEffectiveZoom(1 * ratio);
                                }
                                event.stopImmediatePropagation();
                                $(this._zoomDropdownEl).hide();
                            });
                        } else {
                            optionEl.innerHTML = option + '%';
                            $(optionEl).on('click', (event) => {
                                this.setRealWorldZoom(option / 100, false);
                                event.stopImmediatePropagation();
                                $(this._zoomDropdownEl).hide();
                            });
                        }

                        this._zoomDropdownEl.prepend(optionEl);
                    }

                    this._zoomContainerEl.append(this._zoomDropdownEl);
                    this._zoomContainerEl.addEventListener('click', () => {
                        if ($(this._zoomDropdownEl).is(':visible')) {
                            $(this._zoomDropdownEl).hide();
                        } else {
                            $(this._zoomDropdownEl).show();
                        }
                    });
                }

                this._zoomContainerEl.append(zoomViewerEl);
                this.wrapperEl.append(this._zoomContainerEl);

                // Initialize panning scrollbars
                this._initializePanScrollbars();
            }

            // Deselect groups when any object is selected or selection is cleared
            this.on('selection:created', () => {
                this.discardActiveGroups();
            });
            this.on('selection:cleared', () => {
                this.discardActiveGroups();
            });
            this.on('selection:updated', () => {
                this.discardActiveGroups();
            });

            /**
             * When canvas resolutions get really large and they are scaled down by using setViewportTransform() strokes of 1px
             *  or even 40px are not drawn due to the pixel mapping to the screen. This method returns a stroke width guaranteed
             *  to be visible in the given canvas.
             */
            this.getVisibleStrokeWidth = function () {
                const nonScaledWidth = this.getWidth() / this.viewportTransform[0];
                const nonScaledHeight = this.getHeight() / this.viewportTransform[3];
                const nonScaledSize = Math.min(nonScaledHeight, nonScaledWidth);
                const scaledSize = Math.min(this.getWidth(), this.getHeight());

                const minimumWidth = nonScaledSize / scaledSize;
                return minimumWidth / 3;
            };

            this.getDashedArray = function (spacingFactor = 25) {
                const stroke = this.getVisibleStrokeWidth();
                return [stroke * spacingFactor, stroke * spacingFactor];
            };

            this.updateCornerSize = function (object) {
                if (!object) {
                    return;
                }

                // Canvas width and height with transformations applied
                const canvasWidth = this.getWidth();
                const canvasHeight = this.getHeight();

                // Get object width without transformation because controls size doesn't have transformations applied
                const width =
                    ((object.getWidth && object.getWidth()) || object.width * object.scaleX) *
                    this.viewportTransform[0];
                const height =
                    ((object.getHeight && object.getHeight()) || object.height * object.scaleY) *
                    this.viewportTransform[3];

                // Corners size is proportional to the average canvas dimension
                let cornerSize = 0.015 * ((canvasWidth + canvasHeight) / 2);

                // If the corner size is too close to the element width or height make it smaller
                if (cornerSize * 6 + cornerSize >= width) {
                    cornerSize = width / 5;
                } else if (cornerSize * 6 + cornerSize >= height) {
                    cornerSize = height / 5;
                }

                // We don't want unselectable corners
                if (cornerSize < 1) {
                    cornerSize = 1;
                }

                object.set('transparentCorners', true);
                object.set('cornerSize', cornerSize);
            };

            const throttledResize = throttle(() => {
                this._updateWorkspaceSize();
            }, 200);

            const $wrapper = $(this.wrapperEl).parent().parent();
            if ($wrapper.length) {
                const element = $wrapper.get(0);
                this._resizeSensor = new ResizeSensor(element, () => {
                    throttledResize();
                });

                this._visibilitySensor = respondToVisibility(element, () => {
                    throttledResize();
                });
            } else {
                console.warn(`Couldn't find parent for canvas wrapper element`);
            }

            if (this.displayGuidelines) {
                this._guidelines = new FabricAlignmentGuidelines(this);
                this._guidelines.init();
            }

            if (this.enableContextMenu) {
                this._contextMenu = new FabricContextMenu(this);
                this._contextMenu.init();
            }

            this._clipboard = new FabricClipboard(this);
            this._clipboard.init();

            setTimeout(() => {
                throttledResize();
            }, 0);
        },

        _updatePanScrollbars: function () {
            const panLimitHalf = Math.max(this._workspaceWidth, this._workspaceHeight) * this.panLimit;

            if (this._scroll.horizontalScrollbarEl) {
                // Set scrollbar position relative to the artwork position
                const scrollWidth = $(this._scroll.horizontalScrollbarEl).width();
                const scrollContainerWidth = $(this._scroll.horizontalScrollbarEl).parent().width() - scrollWidth;

                let workspaceWidth = panLimitHalf * 2;
                if (this.preventOutsideArtworkZoom) {
                    const spacing =
                        (this._workspaceWidth - (this._artworkWidth * this.getZoom()) / this.fullVisibilityZoom) / 2;
                    workspaceWidth = (Math.abs(spacing) + panLimitHalf) * 2;
                }

                const artworkCenterOffsetX = this.getPan().x + workspaceWidth / 2;
                let offsetX = scrollContainerWidth * (artworkCenterOffsetX / workspaceWidth);
                offsetX = scrollContainerWidth - offsetX;
                $(this._scroll.horizontalScrollbarEl).css('transform', `translateX(${offsetX}px)`);
            }

            if (this._scroll.verticalScrollbarEl) {
                // Set scrollbar position relative to the artwork position
                const scrollHeight = $(this._scroll.verticalScrollbarEl).height();
                const scrollContainerHeight = $(this._scroll.verticalScrollbarEl).parent().height() - scrollHeight;

                let workspaceHeight = panLimitHalf * 2;
                if (this.preventOutsideArtworkZoom) {
                    const spacing =
                        (this._workspaceHeight - (this._artworkHeight * this.getZoom()) / this.fullVisibilityZoom) / 2;
                    workspaceHeight = (Math.abs(spacing) + panLimitHalf) * 2;
                }

                const artworkCenterOffsetY = this.getPan().y + workspaceHeight / 2;
                let offsetY = scrollContainerHeight * (artworkCenterOffsetY / workspaceHeight);
                offsetY = scrollContainerHeight - offsetY;
                $(this._scroll.verticalScrollbarEl).css('transform', `translateY(${offsetY}px)`);
            }
        },

        _updatePanFromScrollbarPosition: function () {
            const panLimitHalf = Math.max(this._workspaceWidth, this._workspaceHeight) * this.panLimit;

            if (this._scroll.horizontalScrollbarEl) {
                const scrollWidth = $(this._scroll.horizontalScrollbarEl).width();
                const scrollContainerWidth = $(this._scroll.horizontalScrollbarEl).parent().width() - scrollWidth;
                let scrollOffsetX = new WebKitCSSMatrix($(this._scroll.horizontalScrollbarEl).css('transform')).e;
                scrollOffsetX = scrollContainerWidth - scrollOffsetX;

                let workspaceWidth = panLimitHalf * 2;
                if (this.preventOutsideArtworkZoom) {
                    const spacing =
                        (this._workspaceWidth - (this._artworkWidth * this.getZoom()) / this.fullVisibilityZoom) / 2;
                    workspaceWidth = (Math.abs(spacing) + panLimitHalf) * 2;
                }

                const panX = (scrollOffsetX / scrollContainerWidth) * workspaceWidth - workspaceWidth / 2;
                const currentPan = this.getPan();
                this.setPan(panX, currentPan.y);
            }

            if (this._scroll.verticalScrollbarEl) {
                const scrollHeight = $(this._scroll.verticalScrollbarEl).height();
                const scrollContainerHeight = $(this._scroll.verticalScrollbarEl).parent().height() - scrollHeight;
                let scrollOffsetY = new WebKitCSSMatrix($(this._scroll.verticalScrollbarEl).css('transform')).f;
                scrollOffsetY = scrollContainerHeight - scrollOffsetY;

                let workspaceHeight = panLimitHalf * 2;
                if (this.preventOutsideArtworkZoom) {
                    const spacing =
                        (this._workspaceHeight - (this._artworkHeight * this.getZoom()) / this.fullVisibilityZoom) / 2;
                    workspaceHeight = (Math.abs(spacing) + panLimitHalf) * 2;
                }

                const panY = (scrollOffsetY / scrollContainerHeight) * workspaceHeight - workspaceHeight / 2;
                const currentPan = this.getPan();
                this.setPan(currentPan.x, panY);
            }
        },

        _setScrollThumbListener: function (type, element) {
            let startMouse = 0,
                startOffset = 0;
            let isDown = false;

            const downListener = (event) => {
                event.preventDefault();
                event.stopImmediatePropagation();

                if (type === 'horizontal') {
                    startMouse = event.clientX;
                    startOffset = new WebKitCSSMatrix($(element).css('transform')).e;
                } else {
                    startMouse = event.clientY;
                    startOffset = new WebKitCSSMatrix($(element).css('transform')).f;
                }
                isDown = true;
            };
            this._scroll.downListeners.push(downListener);
            element.addEventListener('mousedown', downListener);

            const upListener = () => {
                isDown = false;
            };
            this._scroll.upListeners.push(upListener);
            window.addEventListener('mouseup', upListener, {
                passive: false
            });

            const moveListener = (event) => {
                if (isDown) {
                    if (type === 'horizontal') {
                        if (this.preventOutsideArtworkZoom && this._disableHorizontalScroll) {
                            return;
                        }

                        let offset = startOffset + (event.clientX - startMouse);
                        const maxWidth = $(element).parent().width() - $(element).width();

                        if (offset < 0) {
                            offset = 0;
                        } else if (offset > maxWidth) {
                            offset = maxWidth;
                        }
                        $(element).css('transform', `translateX(${offset}px)`);
                    } else {
                        if (this.preventOutsideArtworkZoom && this._disableVerticalScroll) {
                            return;
                        }

                        let offset = startOffset + (event.clientY - startMouse);
                        const maxHeight = $(element).parent().height() - $(element).height();

                        if (offset < 0) {
                            offset = 0;
                        } else if (offset > maxHeight) {
                            offset = maxHeight;
                        }

                        $(element).css('transform', `translateY(${offset}px)`);
                    }

                    this._updatePanFromScrollbarPosition();
                    this.requestRenderAll();
                }
            };
            this._scroll.moveListeners.push(moveListener);
            window.addEventListener('mousemove', moveListener, {
                passive: false
            });
        },

        _initializePanScrollbars: function () {
            // Horizontal scrollbar
            const hScrollBarContainer = document.createElement('div');
            hScrollBarContainer.classList.add('horizontal-scrollbar-container', 'scrollbar-container');
            if (this.seamLessScrollbars) hScrollBarContainer.classList.add('seamless');
            this._scroll.horizontalScrollbarEl = document.createElement('div');
            this._scroll.horizontalScrollbarEl.classList.add('scrollbar', 'horizontal-scrollbar');
            hScrollBarContainer.appendChild(this._scroll.horizontalScrollbarEl);

            // Vertical scrollbar
            const vScrollBarContainer = document.createElement('div');
            vScrollBarContainer.classList.add('vertical-scrollbar-container', 'scrollbar-container');
            if (this.seamLessScrollbars) vScrollBarContainer.classList.add('seamless');
            this._scroll.verticalScrollbarEl = document.createElement('div');
            this._scroll.verticalScrollbarEl.classList.add('scrollbar', 'vertical-scrollbar');
            vScrollBarContainer.appendChild(this._scroll.verticalScrollbarEl);

            this.wrapperEl.appendChild(vScrollBarContainer);
            this.wrapperEl.appendChild(hScrollBarContainer);

            this._setScrollThumbListener('horizontal', this._scroll.horizontalScrollbarEl);
            this._setScrollThumbListener('vertical', this._scroll.verticalScrollbarEl);
        },

        _getLimitedCanvasSize: function (canvasWidth, canvasHeight) {
            let ratio = 1;

            if (canvasWidth < this.minCanvasSize || canvasHeight < this.minCanvasSize) {
                ratio = Math.max(canvasWidth / this.minCanvasSize, canvasHeight / this.minCanvasSize);
                canvasWidth = canvasWidth / ratio;
                canvasHeight = canvasHeight / ratio;

                console.log('Scaling up canvas to: ' + parseInt(canvasWidth) + 'x' + parseInt(canvasHeight));
            }

            if (canvasWidth > this.maxCanvasSize || canvasHeight > this.maxCanvasSize) {
                ratio = Math.max(canvasWidth / this.maxCanvasSize, canvasHeight / this.maxCanvasSize);
                canvasWidth = canvasWidth / ratio;
                canvasHeight = canvasHeight / ratio;

                console.log('Scaling down canvas to: ' + parseInt(canvasWidth) + 'x' + parseInt(canvasHeight));
            }

            return {
                width: canvasWidth,
                height: canvasHeight,
                scale: 1 / ratio
            };
        },

        _updateWorkspaceSize: function () {
            const $wrapper = $(this.wrapperEl).parent().parent();
            if ($wrapper.length) {
                const wrapperW = $wrapper.width();
                const wrapperH = $wrapper.height();
                const dimensions = calculateCanvasDimensions(wrapperW, wrapperH, this);

                if (dimensions.width && dimensions.height) {
                    this._workspaceExtraScalingX = wrapperW / dimensions.width;
                    this._workspaceExtraScalingY = wrapperH / dimensions.height;
                    let limitedSize = null;

                    // Calculate the new workspace size and offsets
                    this._workspaceWidth = this._artworkWidth * this._workspaceExtraScalingX;
                    this._workspaceHeight = this._artworkHeight * this._workspaceExtraScalingY;

                    if (this.limitCanvasSize) {
                        limitedSize = this._getLimitedCanvasSize(this._workspaceWidth, this._workspaceHeight);
                        this._workspaceWidth = limitedSize.width;
                        this._workspaceHeight = limitedSize.height;
                        this._artworkWidth = this._artworkWidth * limitedSize.scale;
                        this._artworkHeight = this._artworkHeight * limitedSize.scale;
                    }

                    this._artworkOffsetX = (this._workspaceWidth - this._artworkWidth) / 2;
                    this._artworkOffsetY = (this._workspaceHeight - this._artworkHeight) / 2;
                    this.setWidth(this._workspaceWidth);
                    this.setHeight(this._workspaceHeight);

                    if (this.limitCanvasSize) {
                        this.setZoom(limitedSize.scale);
                        this.fullVisibilityZoom = limitedSize.scale;
                    }

                    $(this.lowerCanvasEl).css('width', wrapperW - SCROLLBAR_WIDTH_PX);
                    $(this.upperCanvasEl).css('width', wrapperW - SCROLLBAR_WIDTH_PX);
                    $(this.wrapperEl).css('width', wrapperW);
                    $(this.lowerCanvasEl).css('height', wrapperH - SCROLLBAR_WIDTH_PX);
                    $(this.upperCanvasEl).css('height', wrapperH - SCROLLBAR_WIDTH_PX);
                    $(this.wrapperEl).css('height', wrapperH);

                    this.canvasElWidthMm = $(this.upperCanvasEl).width() * (1 / 96) * 25.4;
                    this.setEffectiveZoom(0.9);
                }
            }
        },

        _updateZoomViewer: function () {
            if (this._zoomTextEl) {
                $(this._zoomTextEl).text(`${parseInt(Math.round(this.getRealWorldZoom() * 100))}%`);
            }
            this._updatePanScrollbars();
        },

        _limitPanning: function () {
            // Make sure can't zoom in/out on spaces outside canvas
            if (this.preventOutsideArtworkZoom) {
                const EPSILON = 0.000001;
                const zoom = this.getZoom();

                this._disableVerticalScroll = false;
                this._disableHorizontalScroll = false;

                // If less than 100% zoom, center the artwork in the workspace. Compare in this way to account for small floating point
                //  errors
                if (zoom - this.fullVisibilityZoom < -EPSILON) {
                    this.centerArtwork();
                    this._disableVerticalScroll = true;
                    this._disableHorizontalScroll = true;
                } else {
                    const d = Math.max(this._workspaceWidth, this._workspaceHeight) * this.panLimit;

                    // Artwork is not as wide as the workspace, just keep it centered horizontally
                    if (
                        this._workspaceWidth - (this._artworkWidth * this.getZoom()) / this.fullVisibilityZoom >
                        EPSILON
                    ) {
                        this.centerArtwork({
                            onlyHorizontal: true
                        });
                        this._disableHorizontalScroll = true;
                    } else {
                        // Artwork is wider than workspace, allow a little scroll up to the edge of the artwork and a little more
                        const spacing =
                            (this._workspaceWidth - (this._artworkWidth * this.getZoom()) / this.fullVisibilityZoom) /
                            2;
                        const pan = this.getPan();
                        const lowerLimit = spacing - d;
                        const upperLimit = -spacing + d;

                        if (pan.x < lowerLimit) {
                            this.setPan(lowerLimit, pan.y);
                        } else if (pan.x > upperLimit) {
                            this.setPan(upperLimit, pan.y);
                        }
                    }

                    // Artwork is not as tall as the workspace, just keep it centered vertically
                    if (
                        this._workspaceHeight - (this._artworkHeight * this.getZoom()) / this.fullVisibilityZoom >
                        EPSILON
                    ) {
                        this.centerArtwork({
                            onlyVertical: true
                        });
                        this._disableVerticalScroll = true;
                    } else {
                        // Artwork is taller than workspace, allow a little scroll up to the edge of the artwork and a little more
                        const spacing =
                            (this._workspaceHeight - (this._artworkHeight * this.getZoom()) / this.fullVisibilityZoom) /
                            2;
                        const pan = this.getPan();
                        const lowerLimit = spacing - d;
                        const upperLimit = -spacing + d;

                        if (pan.y < lowerLimit) {
                            this.setPan(pan.x, lowerLimit);
                        } else if (pan.y > upperLimit) {
                            this.setPan(pan.x, upperLimit);
                        }
                    }
                }

                // Show/hide scroll thumbs when they are enabled/disabled
                if (this._scroll.horizontalScrollbarEl) {
                    if (this._disableHorizontalScroll) {
                        $(this._scroll.horizontalScrollbarEl).css('visibility', 'hidden');
                    } else {
                        $(this._scroll.horizontalScrollbarEl).css('visibility', '');
                    }
                }

                if (this._scroll.verticalScrollbarEl) {
                    if (this._disableVerticalScroll) {
                        $(this._scroll.verticalScrollbarEl).css('visibility', 'hidden');
                    } else {
                        $(this._scroll.verticalScrollbarEl).css('visibility', '');
                    }
                }
            } else {
                let pan = this.getPan();
                const d = Math.max(this._workspaceWidth, this._workspaceHeight) * this.panLimit;
                if (pan.x > d) {
                    this.setPan(d, pan.y);
                    pan = this.getPan();
                } else if (pan.x < -d) {
                    this.setPan(-d, pan.y);
                    pan = this.getPan();
                }
                if (pan.y > d) {
                    this.setPan(pan.x, d);
                } else if (pan.y < -d) {
                    this.setPan(pan.x, -d);
                }
            }
        },

        getPan: function () {
            const centerOffsetX =
                (this._workspaceWidth - (this._artworkWidth * this.getZoom()) / this.fullVisibilityZoom) / 2 -
                this._artworkOffsetX;
            const centerOffsetY =
                (this._workspaceHeight - (this._artworkHeight * this.getZoom()) / this.fullVisibilityZoom) / 2 -
                this._artworkOffsetY;

            return {
                x: this.viewportTransform[4] - centerOffsetX,
                y: this.viewportTransform[5] - centerOffsetY
            };
        },

        setPan: function (x, y) {
            const centerOffsetX =
                (this._workspaceWidth - (this._artworkWidth * this.getZoom()) / this.fullVisibilityZoom) / 2 -
                this._artworkOffsetX;
            const centerOffsetY =
                (this._workspaceHeight - (this._artworkHeight * this.getZoom()) / this.fullVisibilityZoom) / 2 -
                this._artworkOffsetY;

            this.viewportTransform[4] = x + centerOffsetX;
            this.viewportTransform[5] = y + centerOffsetY;
        },

        getEffectiveZoom: function () {
            return this.getZoom() / this.fullVisibilityZoom;
        },

        getRealWorldZoom: function () {
            let zoomH = 1,
                zoomV = 1;
            const currentZoom = this.getEffectiveZoom();

            // Horizontal zoom
            {
                const canvasElSizeMm = $(this.upperCanvasEl).width() * (1 / 96) * 25.4;
                const artworkSizePx = this._artworkWidth / this.fullVisibilityZoom;
                const artworkSizeMm = artworkSizePx * (1 / this.DPI) * 25.4;

                const artworkSizeCurrentPx = currentZoom * artworkSizePx;
                const artworkSizeCurrentMm = (artworkSizeCurrentPx * canvasElSizeMm) / artworkSizePx;
                zoomH = artworkSizeCurrentMm / artworkSizeMm;
            }

            // Vertical zoom
            {
                const canvasElSizeMm = $(this.upperCanvasEl).height() * (1 / 96) * 25.4;
                const artworkSizePx = this._artworkHeight / this.fullVisibilityZoom;
                const artworkSizeMm = artworkSizePx * (1 / this.DPI) * 25.4;

                const artworkSizeCurrentPx = currentZoom * artworkSizePx;
                const artworkSizeCurrentMm = (artworkSizeCurrentPx * canvasElSizeMm) / artworkSizePx;
                zoomV = artworkSizeCurrentMm / artworkSizeMm;
            }

            return Math.min(zoomH, zoomV);
        },

        /**
         * Set the real world scale. A 100% zoom will make the artwork area real size, that is, if the artwork is set to 100mm
         *  it will have a 100mm size when measured with a ruler in the screen (approximately, see comments below). It's important
         *  to correctly set the DPI property in the canvas for this to work.
         * @param {number} zoom zoom amount where 1 is 100% zoom.
         * @param {boolean} centerArtwork wether to center the artwork when zooming
         */
        setRealWorldZoom: function (zoom, centerArtwork) {
            let zoomH = 1,
                zoomV = 1;

            // Horizontal zoom
            {
                // Canvas element size in mm. We assume the density is 96 DPI which is what the browser uses but it's WRONG, sadly
                //  there's no way to detect the real screen DPI without asking the user to enter the screen size in inches. See
                //  this demo for reference: https://ehikioya.com/online-ruler/
                const canvasElSizeMm = $(this.upperCanvasEl).width() * (1 / 96) * 25.4;
                const artworkSizePx = this._artworkWidth / this.fullVisibilityZoom;
                const artworkSizeMm = artworkSizePx * (1 / this.DPI) * 25.4;

                const targetSizeMm = artworkSizeMm * zoom;
                const artworkSizeTargetPx = (targetSizeMm * artworkSizePx) / canvasElSizeMm;

                zoomH = artworkSizeTargetPx / artworkSizePx;
            }

            // Vertical zoom
            {
                // Canvas element size in mm. We assume the density is 96 DPI which is what the browser uses but it's WRONG, sadly
                //  there's no way to detect the real screen DPI without asking the user to enter the screen size in inches. See
                //  this demo for reference: https://ehikioya.com/online-ruler/
                const canvasElSizeMm = $(this.upperCanvasEl).height() * (1 / 96) * 25.4;
                const artworkSizePx = this._artworkHeight / this.fullVisibilityZoom;
                const artworkSizeMm = artworkSizePx * (1 / this.DPI) * 25.4;

                const targetSizeMm = artworkSizeMm * zoom;
                const artworkSizeTargetPx = (targetSizeMm * artworkSizePx) / canvasElSizeMm;

                zoomV = artworkSizeTargetPx / artworkSizePx;
            }

            this.setEffectiveZoom(Math.max(zoomH, zoomV), centerArtwork);
        },

        /**
         * Set the effective zoom for the canvas. This depends on the fullVisibilityZoom property. For example, if fullVisibilityZoom is 0.8 a 100% zoom
         *  will scale the canvas by 0.8 so the entire artwork is visible in the workspace.
         * @param {number} zoom zoom amount where 1 is 100% zoom.
         * @param {boolean} centerArtwork wether to center the artwork when zooming
         */
        setEffectiveZoom: function (zoom, centerArtwork = true) {
            if (centerArtwork) this.centerArtwork();

            // Always zoom using the current viewport center
            this.zoomToPoint(
                {
                    x: this.getWidth() / 2,
                    y: this.getHeight() / 2
                },
                zoom * this.fullVisibilityZoom
            );

            if (centerArtwork) this.centerArtwork();
            this._limitPanning();
            this._updateZoomViewer();
        },

        centerArtwork: function (options) {
            options = options || {
                onlyHorizontal: true,
                onlyVertical: true
            };

            if (options.onlyHorizontal) {
                this.viewportTransform[4] =
                    (this._workspaceWidth - (this._artworkWidth * this.getZoom()) / this.fullVisibilityZoom) / 2 -
                    this._artworkOffsetX;
            }

            if (options.onlyVertical) {
                this.viewportTransform[5] =
                    (this._workspaceHeight - (this._artworkHeight * this.getZoom()) / this.fullVisibilityZoom) / 2 -
                    this._artworkOffsetY;
            }
            /*if (options.onlyHorizontal || options.onlyVertical) {
                this.setViewportTransform(this.viewportTransform);
            }*/
        },

        setArtworkSize: function (width, height, udpateGuidelines = true) {
            this._artworkWidth = width;
            this._artworkHeight = height;
            this._updateWorkspaceSize();

            if (udpateGuidelines) {
                this.setGuidelinesMeasures(width, height);
            }
        },

        setGuidelinesMeasures: function (width, height) {
            this._guidelines.setCanvasMeasures(width, height);
        },

        getArtworkWidth: function () {
            return this._artworkWidth;
        },

        getArtworkHeight: function () {
            return this._artworkHeight;
        },

        getWorkspaceWidth: function () {
            return this._workspaceWidth;
        },

        getWorkspaceHeight: function () {
            return this._workspaceHeight;
        },

        getCenter: function () {
            const canvasWidth = (this.getArtworkWidth() / this.getZoom()) * this.getEffectiveZoom();
            const canvasHeight = (this.getArtworkHeight() / this.getZoom()) * this.getEffectiveZoom();

            return {
                top: canvasHeight / 2,
                left: canvasWidth / 2
            };
        },

        /**
         * Cache common information needed during event processing
         * @private
         * @param {Event} e Event object fired on event
         */
        _cacheTransformEventData: function (e) {
            // Change the viewport transform when saving event data in response to controls interactions to consider the shit of
            //  the artwork area relative to the workspace
            const vptBackup = this.viewportTransform.slice(0);
            this.viewportTransform[4] = this.viewportTransform[4] + this._artworkOffsetX;
            this.viewportTransform[5] = this.viewportTransform[5] + this._artworkOffsetY;

            this.callSuper('_cacheTransformEventData', e);

            this.viewportTransform = vptBackup;
        },

        /**
         * Moves an object to specified level in stack of drawn objects
         * @param {fabric.Object} object Object to send
         * @param {Number} index Position to move to
         * @return {fabric.Canvas} thisArg
         * @chainable
         */
        moveTo: function (object, index) {
            this.callSuper('moveTo', object, index);

            return this;
        },

        addWithoutHistory: function (objects) {
            this.add(objects, false, false);
        },

        add: function () {
            let saveHistory = true;
            let silent = false;
            let objects = [];

            let silentIdx = arguments.length - 1;
            if (arguments.length >= 2 && typeof arguments[silentIdx] === 'boolean') {
                if (typeof arguments[silentIdx - 1] === 'boolean') {
                    saveHistory = arguments[silentIdx];
                    silentIdx--;
                }
                silent = arguments[silentIdx];
                objects = Array.from(arguments).slice(0, silentIdx);
            } else {
                objects = arguments;
            }

            this.callSuper('add', ...objects);

            /**
             * Use for of instead of objects.every() because in some cases
             * for a strange reason it throws that .every() is not a function
             */
            if (this.canDeleteObject) {
                for (const object of objects) {
                    if (!this.canDeleteObject(object)) {
                        saveHistory = false;
                        break;
                    }
                }
            }

            if (saveHistory) {
                this.undoManager?.add(
                    (canvas, objects, ...params) => {
                        this.add.apply(canvas, [...objects, ...params]);
                        // TO DO: change this for the selection method
                        let selection = objects[0];
                        if (objects.length >= 2) {
                            selection = new fabric.ActiveSelection(objects, { canvas });
                            selection.set('lockUniScaling', true);
                        } else {
                            canvas.moveTo(selection, selection.zIndex);
                        }
                        canvas.discardActiveObject();
                        canvas.setActiveObject(selection);
                    },
                    [this, objects, silent, false],
                    this.remove.bind(this),
                    [...objects]
                );
            }

            return this;
        },

        /**
         * Renders both the top canvas and the secondary container canvas.
         * @return {fabric.Canvas} instance
         * @chainable
         */
        renderAll: function () {
            // Only render canvas if not disposed
            if (!this.disposed) {
                return this.callSuper('renderAll');
            }
            return this;
        },

        renderTopLayer: function (ctx) {
            const tmpViewportTransform = this.viewportTransform.slice(0);
            this.viewportTransform[4] = tmpViewportTransform[4] + this._artworkOffsetX;
            this.viewportTransform[5] = tmpViewportTransform[5] + this._artworkOffsetY;

            ctx.save();
            if (this.isDrawingMode && this._isCurrentlyDrawing) {
                this.freeDrawingBrush && this.freeDrawingBrush._render();
                this.contextTopDirty = true;
            }
            // we render the top context - last object
            if (this.selection && this._groupSelector) {
                this._drawSelection(ctx);
                this.contextTopDirty = true;
            }
            ctx.restore();

            this.viewportTransform = tmpViewportTransform;
        },

        /**
         * Renders background, objects, overlay and controls.
         * @param {CanvasRenderingContext2D} ctx
         * @param {Array} objects to render
         * @return {fabric.Canvas} instance
         * @chainable
         */
        renderCanvas: function (ctx, objects) {
            const vptBackup = this.viewportTransform.slice(0);

            this.viewportTransform[4] = this.viewportTransform[4] + this._artworkOffsetX;
            this.viewportTransform[5] = this.viewportTransform[5] + this._artworkOffsetY;
            // Update the viewport transform to consider the artwork offset
            this.setViewportTransform(this.viewportTransform);

            var v = this.viewportTransform,
                path = this.clipPath;
            this.cancelRequestedRender();
            this.calcViewportBoundaries();
            this.clearContext(ctx);
            this.fire('before:render', { ctx: ctx });
            if (this.clipTo) {
                fabric.util.clipContext(this, ctx);
            }

            ctx.save();
            // apply viewport transform once for all rendering process
            ctx.transform(v[0], v[1], v[2], v[3], v[4], v[5]);

            // Render artwork title
            if (this.artworkTitle && this.canvasElWidthMm) {
                const targetMm = 5;
                const artworkSizePx = this._artworkWidth / this.fullVisibilityZoom;
                const artworkSizeTargetPx = (targetMm * artworkSizePx) / this.canvasElWidthMm;

                ctx.font = `bold ${artworkSizeTargetPx}px Helvetica`;
                ctx.fillStyle = 'white';
                ctx.fillText(this.artworkTitle, 0, -15 / this.fullVisibilityZoom);
            }

            // Clip objects outside artwork area but don't clip controls
            const clipRect = new Path2D();
            clipRect.rect(
                0,
                0,
                this._artworkWidth / this.fullVisibilityZoom,
                this._artworkHeight / this.fullVisibilityZoom
            );
            ctx.clip(clipRect);

            // Render background AFTER settings the transformation so the background doesn't occupy the entire canvas when zooming
            this._renderBackground(ctx);

            this._renderObjects(ctx, objects);
            ctx.restore();
            if (!this.controlsAboveOverlay && this.interactive) {
                this.drawControls(ctx);
            }
            if (this.clipTo) {
                ctx.restore();
            }
            if (path) {
                // needed to setup a couple of variables
                path.canvas = this;
                path.shouldCache();
                path._transformDone = true;
                path.renderCache({ forClipping: true });
                this.drawClipPathOnCanvas(ctx);
            }
            this._renderOverlay(ctx);
            if (this.controlsAboveOverlay && this.interactive) {
                this.drawControls(ctx);
            }

            this.viewportTransform = vptBackup;
            this.fire('after:render', { ctx: ctx });
        },

        /**
         * @private
         * @param {CanvasRenderingContext2D} ctx Context to render on
         * @param {string} property 'background' or 'overlay'
         */
        _renderBackgroundOrOverlay: function (ctx, property) {
            var fill = this[property + 'Color'],
                object = this[property + 'Image'],
                v = this.viewportTransform,
                needsVpt = this[property + 'Vpt'];
            if (!fill && !object) {
                return;
            }
            if (fill) {
                ctx.save();

                // Draw background shadow
                if (this.bgShadowColor) {
                    ctx.shadowColor = this.bgShadowColor;
                    ctx.shadowBlur = this.bgShadowBlur;
                }

                ctx.beginPath();
                ctx.moveTo(0, 0);
                ctx.lineTo(this._artworkWidth / this.fullVisibilityZoom, 0);
                ctx.lineTo(this._artworkWidth / this.fullVisibilityZoom, this._artworkHeight / this.fullVisibilityZoom);
                ctx.lineTo(0, this._artworkHeight / this.fullVisibilityZoom);
                ctx.closePath();
                ctx.fillStyle = fill.toLive ? fill.toLive(ctx, this) : fill;
                if (needsVpt) {
                    ctx.transform(
                        v[0],
                        v[1],
                        v[2],
                        v[3],
                        v[4] + (this._artworkOffsetX + fill.offsetX || 0),
                        v[5] + (this._artworkOffsetY + fill.offsetY || 0)
                    );
                }
                var m = fill.gradientTransform || fill.patternTransform;
                m && ctx.transform(m[0], m[1], m[2], m[3], m[4], m[5]);

                ctx.fill();
                ctx.restore();
            }
            if (object) {
                // Draw background shadow
                ctx.save();
                if (this.bgShadowColor) {
                    ctx.shadowColor = this.bgShadowColor;
                    ctx.shadowBlur = this.bgShadowBlur;
                }

                // Draw the background rectangle to apply shadow to the borders
                ctx.beginPath();
                ctx.moveTo(0, 0);
                ctx.lineTo(this._artworkWidth / this.fullVisibilityZoom, 0);
                ctx.lineTo(this._artworkWidth / this.fullVisibilityZoom, this._artworkHeight / this.fullVisibilityZoom);
                ctx.lineTo(0, this._artworkHeight / this.fullVisibilityZoom);
                ctx.closePath();
                ctx.fillStyle = fill.toLive ? fill.toLive(ctx, this) : fill;
                if (needsVpt) {
                    ctx.transform(
                        v[0],
                        v[1],
                        v[2],
                        v[3],
                        v[4] + (this._artworkOffsetX + fill.offsetX || 0),
                        v[5] + (this._artworkOffsetY + fill.offsetY || 0)
                    );
                }
                ctx.fill();
                ctx.restore();

                // Draw the background object (no shadows here)
                ctx.save();
                if (needsVpt) {
                    ctx.transform(1, 0, 0, 1, 0, 0);
                }
                object.render(ctx);
                ctx.restore();
            }
        },

        _zoomWheelListener: function (event) {
            if (event.ctrlKey) {
                const delta = event.deltaY;
                let zoom = this.getZoom();
                zoom = this.getRealWorldZoom() - delta / 1000;

                if (zoom > this.maxZoom) zoom = this.maxZoom;
                if (zoom < this.minZoom) zoom = this.minZoom;

                event.preventDefault();
                event.stopImmediatePropagation();
                this.setRealWorldZoom(zoom, false);
            }
            // Panning
            else {
                const pan = this.getPan();
                let deltaX = event.deltaX;
                let deltaY = event.deltaY;

                event.preventDefault();
                event.stopImmediatePropagation();

                deltaX = pan.x - deltaX / 5;
                deltaY = pan.y - deltaY / 5;

                // Lock movement horizontally
                if (event.shiftKey) {
                    deltaX = pan.x - event.deltaY / 5;
                    deltaY = pan.y;
                }

                this.setPan(deltaX, deltaY);
                this._limitPanning();
                this._updatePanScrollbars();
                this.requestRenderAll();
            }
        },

        _zoomMouseDownListener: function (event) {
            // Hide zoom dropdown when clicking the canvas
            $(this._zoomDropdownEl).hide();

            // Don't trigger drag behaviour if clicking over an element corner so we actions on corner made by using the modifier key
            //  'ctrl' also work
            let target = this.findTarget(event);
            if (
                event.ctrlKey === true &&
                (!target || (target && !target._findTargetCorner(this.getPointer(event, true))))
            ) {
                this.isDragging = true;
                this.selection = false;

                const mousePoint = this.getPointer(event, true);
                this.lastPosX = mousePoint.x;
                this.lastPosY = mousePoint.y;

                event.preventDefault();
                event.stopImmediatePropagation();
            }
        },

        _zoomMouseMoveListener: function (event) {
            if (this.isDragging) {
                const mousePoint = this.getPointer(event, true);

                this.viewportTransform[4] += mousePoint.x - this.lastPosX;
                this.viewportTransform[5] += mousePoint.y - this.lastPosY;

                this._limitPanning();
                this._updatePanScrollbars();
                this.requestRenderAll();
                this.lastPosX = mousePoint.x;
                this.lastPosY = mousePoint.y;
            }
        },

        _zoomMouseUpListener: function () {
            // On mouse up we want to recalculate the new interaction
            //  for all objects, so we call setViewportTransform
            this.setViewportTransform(this.viewportTransform);
            this.isDragging = false;
            this.selection = true;
        },

        copySelection: async function () {
            this.lastClipboardObject = [...this._selectedItems];
            await this._clipboard.registerCustomilyElement();
        },

        pasteCopiedObjects() {
            if (
                (typeof this.enableCopyPaste == 'boolean' && !this.enableCopyPaste) ||
                (typeof this.enableCopyPaste == 'function' && !this.enableCopyPaste(this.getActiveObjects()))
            )
                return;

            return this._cloneSelection(this.lastClipboardObject, {
                shiftElements: true,
                updateElementId: true,
                updateName: false,
                appendId: true,
                saveHistory: true
            });
        },

        _cloneSelection: async function (
            items = undefined,
            options = {
                shiftElements: false,
                updateElementId: false,
                updateName: true,
                appendId: false,
                saveHistory: true,
                omitSelection: false
            }
        ) {
            const targetItems = items || this._selectedItems;
            const newElements = await duplicateItemsDeep({
                items: targetItems,
                options,
                canvas: this
            });
            if (!options.omitSelection) this.fire('selection:cloned', newElements);
            return newElements;
        },

        deleteSelection: function () {
            if (this.canDeleteObject) {
                const objectsCanBeDeleted = (this._selectedItems || []).filter((object) => {
                    return object.element && this.canDeleteObject(object.element);
                });
                if (objectsCanBeDeleted.length === 0) return;
                removeItemsDeep(objectsCanBeDeleted, { canvas: this, saveHistory: true });
            } else {
                removeItemsDeep(this._selectedItems, { canvas: this, saveHistory: true });
            }

            //In order to notifying the delete action to the vue layer instance.
            const deleteEvent = new CustomEvent('customily-shortcut-delete');
            window.dispatchEvent(deleteEvent);
        },

        groupSelectedItems: async function () {
            await groupItemsDeep(this._selectedItems, {
                canvas: this,
                saveHistory: true
            });
        },
        unGroupSelectedItems: function () {
            unGroupItemsDeep(this._selectedItems, {
                canvas: this,
                saveHistory: true
            });
        },

        removeSelection: function (
            options = {
                saveHistory: true
            }
        ) {
            try {
                if (!this._selectedItems?.length) return [];

                return removeItemsDeep(this._selectedItems, {
                    canvas: this,
                    saveHistory: options.saveHistory
                });
            } catch (error) {
                console.error('Error removing selected elements:', error);
                return [];
            }
        },

        // Method to get color at XY coordinate
        getColorAt: function (event) {
            const mouse = this.getPointer(event, true);

            let x = mouse.x * this.getRetinaScaling();
            let y = mouse.y * this.getRetinaScaling();

            // Get the color array for the pixel under the mouse
            const pixel = this.getContext('2d').getImageData(x, y, 1, 1).data;

            return {
                r: pixel[0],
                g: pixel[1],
                b: pixel[2],
                a: pixel[3]
            };
        },

        _onDoubleClick: function (e) {
            // eslint-disable-next-line @typescript-eslint/no-this-alias
            const self = this;

            const target = self.findRealTarget(e);
            self.fire('mouse:dblclick', {
                target: target,
                e: e
            });

            if (target && !self.isDrawingMode) {
                // To unify the behavior, the object's double click event does not fire on drawing mode.
                target.fire('object:dblclick', {
                    e: e
                });
            }
        },

        _containsPointForGroupObject: function (pointer, target) {
            var xy = this._normalizePointer(target, pointer);

            // http://www.geog.ubc.ca/courses/klink/gis.notes/ncgia/u32.html
            // http://idav.ucdavis.edu/~okreylos/TAship/Spring2000/PointInPolygon.html
            return target.containsPoint(xy) || target._findTargetCorner(pointer);
        },

        _checkTargetForGroupObject: function (obj, pointer) {
            if (obj && obj.visible && obj.evented && this._containsPointForGroupObject(pointer, obj)) {
                if ((this.perPixelTargetFind || obj.perPixelTargetFind) && !obj.isEditing) {
                    var isTransparent = this.isTargetTransparent(obj, pointer.x, pointer.y);
                    if (!isTransparent) {
                        return true;
                    }
                } else {
                    return true;
                }
            }
        },

        _adjustPointerAccordingToGroupObjects: function (originalPointer, group) {
            var groupObjects = group._objects;
            var objectLength = groupObjects.length;
            if (objectLength <= 0) {
                return originalPointer;
            }

            var minLeft = 99999;
            var minTop = 99999;

            var i;
            for (i = 0; i < objectLength; i++) {
                var obj = groupObjects[i];
                if (minLeft > obj.left) {
                    minLeft = obj.left;
                }

                if (minTop > obj.top) {
                    minTop = obj.top;
                }
            }

            originalPointer.x += minLeft - group.left;
            originalPointer.y += minTop - group.top;

            return originalPointer;
        },

        findRealTarget: function (e) {
            // eslint-disable-next-line @typescript-eslint/no-this-alias
            var self = this;
            var target;
            if (!self.fireEventForObjectInsideGroup) {
                target = self.findTarget(e);
            } else {
                // Skip group to find the real object.
                target = self.findTarget(e, true);
                if (target !== undefined && target._objects !== undefined) {
                    var pointer = self.getPointer(e, true);
                    var objects = target._objects;
                    pointer = self._adjustPointerAccordingToGroupObjects(pointer, target);
                    var i = objects.length;
                    while (i--) {
                        if (self._checkTargetForGroupObject(objects[i], pointer)) {
                            target = objects[i];

                            break;
                        }
                    }
                }
            }

            return target;
        },

        toDataURL: function (options) {
            options = options || {};

            // Crop the resulting image so only the artwork is included
            options.left = this.viewportTransform[4] + this._artworkOffsetX;
            options.top = this.viewportTransform[5] + this._artworkOffsetY;
            options.width = (this._artworkWidth * this.getZoom()) / this.fullVisibilityZoom;
            options.height = (this._artworkHeight * this.getZoom()) / this.fullVisibilityZoom;

            return this.callSuper('toDataURL', options);
        },

        removeGroup: function (groupOrId) {
            if (!this.enableGroups) {
                return;
            }

            const index =
                typeof groupOrId === 'string'
                    ? this.elementsGroups.findIndex((g) => g.id === groupOrId)
                    : this.elementsGroups.indexOf(groupOrId);

            if (index >= 0) {
                const group = this.elementsGroups[index];
                this.elementsGroups.splice(index, 1);
                this.fire('group:removed', {
                    group: group
                });
                return group;
            }
        },

        addGroup: function (group, silent = false) {
            if (!this.enableGroups) {
                return;
            }

            // If the group doesn't exists, add it to the canvas
            if (!this.elementsGroups.some((g) => g.id === group.id)) {
                this.elementsGroups.push(group);
                if (!silent) {
                    this.fire('group:added', {
                        group: group
                    });
                }
            }
        },

        getMaxPositionGroup: function () {
            if (!this.enableGroups) {
                return 0;
            }

            const groups = this.elementsGroups;
            if (!groups.length) {
                return 1;
            }

            return Math.max(...groups.map((group) => group?.position)) + 1;
        },

        discardActiveGroups: function () {
            if (this._selectedGroups.length) {
                const groups = this._selectedGroups;
                this._selectedGroups = [];

                for (const group of groups) {
                    this.fire('group:deselected', {
                        target: group
                    });
                    this.getElementsGroups()
                        .filter((g) => g.parentId === group.id)
                        .forEach((group) => {
                            this.fire('group:deselected', {
                                target: group
                            });
                        });
                }
            }
        },

        getActiveGroups: function () {
            return [...this._selectedGroups];
        },

        getElementsGroups: function () {
            return [...this.elementsGroups];
        },

        // Helper method to extract group ID
        _extractGroupId: function (groupOrId) {
            if (!groupOrId) return null;
            return typeof groupOrId === 'string' ? groupOrId : groupOrId.id;
        },

        getGroupElements: function (groupOrId, visitedUuid = false) {
            if (!this.enableGroups) return [];

            // Validate and extract group ID
            const groupId = this._extractGroupId(groupOrId);
            if (!groupId) return [];

            // Get all child groups
            const childGroupIds = this.getElementsGroups()
                .filter((group) => group.parentId === groupId)
                .map((group) => group.id);

            // Find all elements belonging to this group or its children
            let groupElements = this.getObjects().filter(
                (element) => element.groupId === groupId || childGroupIds.includes(element.groupId)
            );

            // Fallback to UUID search if no elements found
            if (!visitedUuid && !groupElements.length && groupOrId?.uuid) {
                groupElements = this.getGroupElementsByUuid(groupOrId.uuid);
            }

            return groupElements;
        },

        getGroupElementsByUuid: function (uuid) {
            if (!this.enableGroups || !uuid) return [];

            const targetGroup = this.getElementsGroups().find((group) => group.uuid === uuid);

            const visitedUuid = true;
            return targetGroup ? this.getGroupElements(targetGroup, visitedUuid) : [];
        },

        /**
         * This temporarily deselects all objects in the canvas (without firing any events) and re-selects them
         *  (again, without firing events) after the callback is executed.
         * @param {Function} callback
         */
        temporaryDiscardActiveObject: function (callback) {
            const objects = this.getActiveObjects();

            // Discard active object without firing events
            this._discardActiveObject();
            callback();

            // Re-select active object without firing events
            const selection = new fabric.ActiveSelection(objects, {
                canvas: this
            });
            selection.set('lockUniScaling', true);
            this._setActiveObject(selection);
        },

        /**
         * Gets the highest z-index among all canvas objects
         * @returns {number} The highest z-index value, or 0 if no objects exist
         */
        getHighestZIndex: function () {
            try {
                const elements = [
                    ...this.getObjects(),
                    ...this.getElementsGroups(),
                    ...this.getElementsPageEps()
                ].filter(Boolean);

                if (!elements.length) {
                    return 0;
                }

                return Math.max(...elements.map((obj) => obj.zIndex || 0));
            } catch (error) {
                console.error('Error getting highest z-index:', error);
                return 0;
            }
        },

        addPageEps: function (page, silent = false, saveHistory = true) {
            if (!this.enablePageEps) {
                console.warn('Page EPS feature is disabled.');
                return;
            }
            if (!this.elementsPageEps.some((g) => g.id === page.id)) {
                this.elementsPageEps.push(page);
                if (!silent) {
                    this.fire('pageeps:added', {
                        page
                    });
                }
                if (saveHistory) {
                    this.undoManager?.add(
                        this.addPageEps.bind(this),
                        [page, false, false],
                        this.removePageEps.bind(this),
                        [page, false]
                    );
                }
            }
        },

        getActivePageEps: function () {
            return [...this._selectedPageEps];
        },

        getElementsPageEps: function () {
            return [...this.elementsPageEps];
        },

        getPageEpsById: function (pageEpsId) {
            return this.getElementsPageEps().find((s) => s.id == pageEpsId);
        },

        getFirstActivePageEpsId: function () {
            if (!this.enablePageEps) return null;
            const page = this.getActivePageEps();
            return page.length > 0 ? page[0].id : null;
        },

        getFirstActivePageEps: function () {
            if (!this.enablePageEps) return null;
            const page = this.getActivePageEps();
            return page.length > 0 ? page[0] : null;
        },

        getPageEpsElements: function (pageEpsOrId) {
            const elements =
                typeof pageEpsOrId === 'string'
                    ? this.getObjects().filter((e) => e.pageEpsId === pageEpsOrId)
                    : this.getObjects().filter((e) => e.pageEpsId === pageEpsOrId.id);

            return elements;
        },

        setActiveFirstPageEps: function (forceSelection = true) {
            if (!this.enablePageEps) return;
            const page = this.getElementsPageEps();
            if (page.length > 0) {
                this.setActivePageEps(page[0], forceSelection);
            }
        },

        removePageEps: function (pageEpsOrId, saveHistory = true) {
            if (!this.enablePageEps) return;
            const pageEps = typeof pageEpsOrId === 'string' ? this.getPageEpsById(pageEpsOrId) : pageEpsOrId;

            if (!pageEps) return;
            const objects = this.getPageEpsElements(pageEps);
            this.elementsPageEps = this.elementsPageEps.filter((s) => s.id != pageEps.id);
            this.discardActivePageEps(pageEps);
            this.fire('pageeps:removed', {
                target: pageEps
            });

            if (saveHistory) {
                this.undoManager?.add(
                    (objects, pageEps) => {
                        removeElementsByUuid(this, objects);
                        this.removePageEps.apply(this, [pageEps, false]);
                        this.setActiveFirstPageEps.apply(this, [false]);
                    },
                    [objects, pageEps],
                    (objects, pageEps) => {
                        this.addPageEps.apply(this, [pageEps]);
                        this.addActivePageEps.apply(this, [pageEps]);
                        this.add.call(this, ...objects);
                        for (const object of objects) {
                            this.moveTo(object, object.zIndex);
                        }
                    },
                    [objects, pageEps]
                );
            }
        },

        getMaxZindexPageEps: function () {
            const elements = this.getElementsPageEps();
            if (!elements.length) {
                return 0;
            }
            return Math.max(...elements.map((item) => item.zIndex));
        },

        getMinZindexPageEps: function () {
            const elements = this.getElementsPageEps();
            if (!elements.length) {
                return 0;
            }
            return Math.min(...elements.map((item) => item.zIndex));
        },

        getMaxIdPageEps: function () {
            const elements = this.getElementsPageEps();
            if (!elements.length) {
                return 0;
            }
            return Math.max(...elements.map((item) => item.id));
        },

        discardActivePageEps: function () {
            this.discardActiveObject();
            if (this._selectedPageEps.length > 0) {
                const spaces = this._selectedPageEps;
                this._selectedPageEps = [];

                for (const space of spaces) {
                    this.fire('pageeps:deselected', {
                        target: space
                    });
                }
            } else {
                this._selectedPageEps = [];
                this.fire('pageeps:deselected', {
                    target: null
                });
            }
        },

        addActivePageEps: function (pageEpsOrId) {
            if (!this.enablePageEps) return;
            const pageEps = typeof pageEpsOrId == 'object' ? pageEpsOrId : this.getPageEpsById(pageEpsOrId);
            if (pageEps && !this._selectedPageEps.some((g) => g.id === pageEps.id)) {
                this._selectedPageEps = [pageEps];
                this.fire('pageeps:selected', {
                    target: pageEps
                });
            }
        },

        selectElementsFromPageEps: function (pageEpsOrId, forceSelection = true) {
            if (!this.enablePageEps || !forceSelection) return;
            const pageEps = typeof pageEpsOrId == 'object' ? pageEpsOrId : this.getPageEpsById(pageEpsOrId);
            if (pageEps) {
                const elements = this.getObjects().filter((o) => o.pageEpsId === pageEps.id);
                if (elements.length) {
                    selectCanvasElements(elements, this, forceSelection);
                }
            }
        },

        updatePageEps: function (page, obj, select = false) {
            if (!this.enablePageEps || !page) return;
            page = Object.assign(page, obj);
            this.fire('pageeps:update', {
                target: page
            });
            if (select) {
                this.addActivePageEps(page);
            }
        },

        setActivePageEps: function (page) {
            if (!this.enablePageEps) return;

            if (!page || !page.id) return;

            const currentPages = this.getActivePageEps();
            const isActivated = currentPages?.some((p) => p.id === page.id);

            if (!isActivated) {
                this.discardActivePageEps();
                this.addActivePageEps(page);
            }

            const elementsPageEps = this.getElementsPageEps();

            if (!elementsPageEps || elementsPageEps.length === 0) {
                return;
            }

            handlePageEpsStatus(this, [page.id]);
        },

        dispose: function () {
            // Mark canvas as disposed
            this.disposed = true;

            this.upperCanvasEl.removeEventListener('wheel', this._zoomWheelListener);
            this.upperCanvasEl.removeEventListener('mousedown', this._zoomMouseDownListener);
            this.upperCanvasEl.removeEventListener('mousemove', this._zoomMouseMoveListener);
            window.removeEventListener('mouseup', this._zoomMouseUpListener);

            if (this._resizeSensor) {
                this._resizeSensor.detach();
            }

            if (this._visibilitySensor) {
                this._visibilitySensor.disconnect();
            }

            if (this._scroll.horizontalScrollbarEl) {
                for (const listener of this._scroll.moveListeners) {
                    window.removeEventListener('mousemove', listener);
                }
                for (const listener of this._scroll.upListeners) {
                    window.removeEventListener('mouseup', listener);
                }
                for (const listener of this._scroll.downListeners) {
                    this._scroll.horizontalScrollbarEl.removeEventListener('mousedown', listener);
                }
            }

            if (this._scroll.verticalScrollbarEl) {
                for (const listener of this._scroll.moveListeners) {
                    window.removeEventListener('mousemove', listener);
                }
                for (const listener of this._scroll.upListeners) {
                    window.removeEventListener('mouseup', listener);
                }
                for (const listener of this._scroll.downListeners) {
                    this._scroll.verticalScrollbarEl.removeEventListener('mousedown', listener);
                }
            }
            this._scroll = null;

            this.undoManager?.clear();
            this._guidelines?.destroy();
            this._clipboard?.destroy();
            this._contextMenu?.destroy();

            this.callSuper('dispose');
        },

        /**
         * Creates a deep clone of a fabric object
         * @param {fabric.Object} object The fabric object to clone
         * @param {boolean} [markAsCloned=false] Flag to mark object as cloned
         * @returns {Promise<fabric.Object>} Cloned fabric object
         * @throws {Error} If source object is invalid or cloning fails
         */
        cloneObject: async function (object, markAsCloned = false) {
            if (!object || typeof object.clone !== 'function') {
                throw new Error('Invalid source object provided');
            }

            try {
                return new Promise((resolve, reject) => {
                    object.clone((cloned) => {
                        if (!cloned) {
                            reject(new Error('Clone operation failed'));
                            return;
                        }
                        const deepClone = cloneDeep(cloned);
                        if (markAsCloned) {
                            deepClone.isCloned = true;
                        }
                        // Ensure coordinates and state are reset
                        deepClone.setCoords();
                        resolve(deepClone);
                    });
                });
            } catch (error) {
                throw new Error(`Cloning failed: ${error.message}`);
            }
        },

        cloneGroup: function (group) {
            try {
                const clonedGroup = cloneDeep(group);
                clonedGroup.isCloned = true;

                return clonedGroup;
            } catch (error) {
                console.error('Error cloning group:', error);
                throw error;
            }
        },

        clonePageEps: function (pageEps) {
            try {
                const clonedPageEps = cloneDeep(pageEps);
                clonedPageEps.isCloned = true;

                return clonedPageEps;
            } catch (error) {
                console.error('Error cloning page eps:', error);
                throw error;
            }
        }
    });
})();
